import React from 'react';
import { useQuery } from '@tanstack/react-query';
import authApi from '../services/auth.api';
import { clearStoredToken, getStoredToken, setStoredToken } from '../lib/auth';

const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = React.useState(() => getStoredToken());

  const sessionQuery = useQuery({
    queryKey: ['admin-session', token],
    queryFn: () => authApi.me(),
    enabled: Boolean(token),
    retry: false,
  });

  const login = React.useCallback((nextToken) => {
    setStoredToken(nextToken);
    setToken(nextToken);
  }, []);

  const logout = React.useCallback(() => {
    clearStoredToken();
    setToken(null);
  }, []);

  React.useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener('vin-admin:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('vin-admin:unauthorized', handleUnauthorized);
    };
  }, [logout]);

  const value = React.useMemo(
    () => ({
      token,
      user: sessionQuery.data?.data?.user ?? null,
      isAuthenticated: Boolean(token && sessionQuery.data?.data?.user),
      isCheckingSession: Boolean(token) && sessionQuery.isLoading,
      login,
      logout,
      refetchSession: sessionQuery.refetch,
    }),
    [
      login,
      logout,
      sessionQuery.data,
      sessionQuery.isLoading,
      sessionQuery.refetch,
      token,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
